/*
 * Copyright (c) Nexign, JSC, 2021-2022.
 */
/**
 * AccountInfo Footer Actions
 *
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @version 1.22.0
 * @since 1.11.0
 */

import { Fragment, useCallback, useState } from 'react';
import type { FC } from 'react';

import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import useTableFooterButtons, { IUseTableFooterButtons } from '@components/common/TableFooter/hooks/useTableFooterButtons';
import useTableFooterDisabled from '@components/common/TableFooter/hooks/useTableFooterDisabled';
import {
    TableFooterContainerStyled,
    TableFooterLeftContainerStyled,
    TableFooterRightContainerStyled,
    TableFooterRowContainerStyled,
} from '@components/common/TableFooter/styled/TableFooterStyled';
import TableFooterItem from '@components/common/TableFooter/TableFooterItem';
import TableFooterShowMenu from '@components/common/TableFooter/TableFooterShowMenu';
import { TableFooterNavStyled } from '@components/Table/styled/TableStyled';
import { TableFooterNameType } from '@components/Table/types/tableTypes';
import type { IRouteParams } from '@routes/constants/routesConstants';

import { makeSelectAccountInfoButtonsEnabled } from '../pageAccountInfoSelectors';
import { PageCardFooterStyled } from './styled/PageCardFooterStyled';

const PageAccountInfoFooter: FC = () => {

    const { clientId } = useParams<IRouteParams>();

    const [isOpen, setOpen] = useState(true);

    const buttonsEnabled = useSelector(makeSelectAccountInfoButtonsEnabled(clientId));

    const {
        bills,
        dcRules,
        expenses,
        inquiryCreate,
        mobileCash,
        mobileReport,
        payment,
        reconciliation,
        wizardMoneyTransfer,
    } = useTableFooterButtons(TableFooterNameType.accountId);

    const {
        isBtnDisabled,
        getFooterButtonTooltipMsg,
    } = useTableFooterDisabled(TableFooterNameType.accountId);

    const getTooltipMsg = useCallback((item: IUseTableFooterButtons): string =>
        getFooterButtonTooltipMsg(item.id) || item.tooltipMsg, [getFooterButtonTooltipMsg]);

    const renderBtn = useCallback((item: IUseTableFooterButtons) => buttonsEnabled[item.id] && (
        <TableFooterItem
            {...item}
            isDisabled={isBtnDisabled(item.id)}
            key={item.id}
            tooltipMsg={getTooltipMsg(item)}
        />
    ), [isBtnDisabled, getTooltipMsg, buttonsEnabled]);

    return <PageCardFooterStyled>
        <TableFooterNavStyled>
            <TableFooterContainerStyled>
                <TableFooterShowMenu
                    isOpen={isOpen}
                    onClick={() => {
                        setOpen(!isOpen);
                    }}
                />
                {
                    isOpen && <Fragment>
                        <TableFooterLeftContainerStyled className="table-footer__left-container">
                            <TableFooterRowContainerStyled>
                                {[mobileCash, payment, bills, dcRules].map(renderBtn)}
                            </TableFooterRowContainerStyled>
                            <TableFooterRowContainerStyled>
                                {[inquiryCreate, reconciliation, expenses, wizardMoneyTransfer].map(renderBtn)}
                            </TableFooterRowContainerStyled>
                        </TableFooterLeftContainerStyled>
                        <TableFooterRightContainerStyled className="table-footer__right-container">
                            {[mobileReport].map(renderBtn)}
                        </TableFooterRightContainerStyled>
                    </Fragment>
                }
            </TableFooterContainerStyled>
        </TableFooterNavStyled>
    </PageCardFooterStyled>;
};

export default PageAccountInfoFooter;
